/* eslint-disable react/jsx-one-expression-per-line */
import React, { useState, useCallback } from 'react'
import { Box, Button, Grid, TextField, CircularProgress } from '@mui/material'
import { useLang } from '~/hooks/useLang'
import FilterAutocompleteType from '~/components/FilterAutocompleteType'
import FilterAutocompleteSubtypeByType from '~/components/FilterAutocompleteSubtypeByType'
import FilterAutocompletePeriod from '~/components/FilterAutocompletePeriod'
import { useHookstate } from '@hookstate/core'

// Tipos
interface Filters {
  readonly code: string
  readonly location: string
  readonly type_id: string
  readonly subtype_id: string
  readonly start_date: string
  readonly finish_date: string
}

interface TypeOption {
  readonly id: string | number
  readonly name: string
}

interface SubtypeOption {
  readonly id: number
  readonly name: string
}

interface DispatchUnificationFiltersProps {
  readonly filters: Filters
  readonly onFiltersChange: (
    filters: Filters | ((prevFilters: Filters) => Filters)
  ) => void
  readonly loadingButton: boolean
  readonly clearFilters: () => void
  readonly searchButton: () => void
}

export default function DispatchUnificationFilters({
  filters,
  onFiltersChange,
  loadingButton,
  clearFilters,
  searchButton,
}: DispatchUnificationFiltersProps) {
  const { translate } = useLang()
  const period = useHookstate<string>('')

  const handleFilterChange = (field: keyof Filters, value: string) => {
    onFiltersChange({
      ...filters,
      [field]: value,
    })
  }

  // Use useCallback para evitar recriações desnecessárias
  const onStartDateChange = useCallback(
    (value: any | null) => {

      // Use uma função callback para garantir que temos o estado mais atual
      onFiltersChange((prevFilters: Filters) => ({
        ...prevFilters,
        start_date: value,
      }))
    },
    [onFiltersChange]
  )

  const onFinishDateChange = useCallback(
    (value: any | null) => {
      // Use uma função callback para garantir que temos o estado mais atual
      onFiltersChange((prevFilters: Filters) => ({
        ...prevFilters,
        finish_date: value,
      }))
    },
    [onFiltersChange]
  )

  const handleClearFilters = () => {
    period.set('') // Limpa o período
    clearFilters()
  }

  const handleSearch = () => {
    searchButton()
  }

  return (
    <Box pb={2}>
      <Grid container spacing={2}>
        {/* Código */}
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            size="small"
            label={translate('Code')}
            value={filters.code}
            onChange={(e) => handleFilterChange('code', e.target.value)}
            variant="outlined"
            disabled={loadingButton}
          />
        </Grid>

        {/* Localização */}
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            size="small"
            label={translate('Location')}
            value={filters.location}
            onChange={(e) => handleFilterChange('location', e.target.value)}
            variant="outlined"
            disabled={loadingButton}
          />
        </Grid>

        {/* Tipo */}
        <Grid item xs={12} sm={6} md={3}>
          <FilterAutocompleteType
            disabled={loadingButton}
            typeId={Number(filters.type_id) || null}
            onTypeChange={(type: TypeOption | null) => {
              handleFilterChange('type_id', String(type?.id || ''))
              // Limpar subtipo quando mudar o tipo
              if (filters.subtype_id) {
                handleFilterChange('subtype_id', '')
              }
            }}
          />
        </Grid>

        {/* Subtipo */}
        <Grid item xs={12} sm={6} md={3}>
          <FilterAutocompleteSubtypeByType
            typeId={Number(filters.type_id)}
            subtypeId={Number(filters.subtype_id) || null}
            hasType={!!filters.type_id}
            onSubtypeChange={(subtype: SubtypeOption | null) => {
              handleFilterChange('subtype_id', String(subtype?.id || ''))
            }}
            disabled={loadingButton || !Number(filters.type_id)}
          />
        </Grid>

        {/* Período */}
        <FilterAutocompletePeriod
          onStartDateChange={(value) =>
            onStartDateChange(value?.toJSON() ?? null)
          }
          onFinishDateChange={(value) =>
            onFinishDateChange(value?.toJSON() ?? null)
          }
          onPeriodChange={(value: string) => {
            period.set(value)
          }}
          period={period.get()}
          xsDateBetween={3}
          xsPeriod={3}
          hidePeriod={false}
          marginDateBetween={0}
          disableFuture={false}
        />

        {/* Botões */}
        <Grid
          item
          xs={12}
          sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}
        >
          <Button
            variant="outlined"
            onClick={handleClearFilters}
            disabled={loadingButton}
          >
            {translate('Clear Filters')}
          </Button>
          <Button
            variant="contained"
            onClick={handleSearch}
            disabled={loadingButton}
          >
            {loadingButton ? (
              <CircularProgress size={24} />
            ) : (
              translate('Search')
            )}
          </Button>
        </Grid>
      </Grid>
    </Box>
  )
}
